import {
  ChartMark,
  WidgetKind,
  type Filters,
  type Metric,
  type WidgetOf,
} from 'corso-admin-ui';
import {
  CrewClaimResolutionMethodEnum,
  CrewClaimRollupStatusCode,
  CrewClaimStatusCode,
  crewClaimStatuses,
} from 'corso-types';

// the warehouse stores a line item's status by name, which differs from its code once there's a space
export const statusName = {
  inTransit: crewClaimStatuses[CrewClaimStatusCode.inTransit].name,
  atReturnLocation:
    crewClaimStatuses[CrewClaimStatusCode.atReturnLocation].name,
} as const;

const inTransit = {
  claimStatuses: [statusName.inTransit],
} satisfies Filters<'claimItems'>;
export const readyForReview = {
  statuses: [CrewClaimRollupStatusCode.readyForReview],
} satisfies Filters<'claimItems'>;
export const resolved = {
  statuses: [CrewClaimRollupStatusCode.completed],
  claimStatuses: [
    CrewClaimStatusCode.approved,
    CrewClaimStatusCode.denied,
    CrewClaimStatusCode.closed,
  ],
} satisfies Filters<'claimItems'>;

export const refundOwedSoon = {
  kind: WidgetKind.kpi,
  title: 'Refund amount owed soon',
  description: 'Refund requests on their way back or awaiting review',
  metric: 'itemValue',
  timeField: 'inTransitOn',
  filters: {
    requestedResolutions: [CrewClaimResolutionMethodEnum.refund],
    claimStatuses: [statusName.atReturnLocation, statusName.inTransit],
    statuses: [
      CrewClaimRollupStatusCode.inProgress,
      CrewClaimRollupStatusCode.readyForReview,
    ],
  },
} as const satisfies WidgetOf<'claimItems'>;

export const reasonTables = [
  {
    kind: WidgetKind.table,
    title: 'Submitted reasons',
    description: 'Open a reason to see its details',
    dimensions: ['reason', 'reasonDetail'],
    metrics: ['items', 'requestedAmount'],
    share: 'items',
  },
] as const satisfies WidgetOf<'claimItems'>[];

// a store has more products, variants and SKUs than a table can hold, so these list the top of them
export const productRows = 50;

export const topProducts = {
  kind: WidgetKind.ranking,
  title: 'Top products',
  dimension: 'productTitle',
  metric: 'items',
  limit: 10,
} as const satisfies WidgetOf<'claimItems'>;

export const productTables = [
  {
    kind: WidgetKind.table,
    title: 'Products',
    dimensions: ['productTitle'],
    metrics: ['items', 'itemValue'],
    limit: productRows,
  },
  {
    kind: WidgetKind.table,
    title: 'Variants',
    dimensions: ['productTitle', 'variantTitle', 'sku'],
    metrics: ['items', 'itemValue'],
    limit: productRows,
  },
] as const satisfies WidgetOf<'claimItems'>[];

export const productReasonTables = [
  {
    kind: WidgetKind.table,
    title: 'Reasons by product',
    dimensions: ['productTitle', 'reason'],
    metrics: ['items'],
    limit: productRows,
  },
  {
    kind: WidgetKind.table,
    title: 'Reasons by variant',
    dimensions: ['productTitle', 'variantTitle', 'reason'],
    metrics: ['items'],
    limit: productRows,
  },
] as const satisfies WidgetOf<'claimItems'>[];

export const itemsOverTime = {
  kind: WidgetKind.overTime,
  title: 'Items over time',
  mark: ChartMark.line,
  metrics: ['items'],
} as const satisfies WidgetOf<'claimItems'>;

const resolutionStatus = {
  kind: WidgetKind.ranking,
  title: 'Resolution status',
  dimension: 'claimStatus',
  metric: 'items',
  filters: resolved,
} as const satisfies WidgetOf<'claimItems'>;

const timeFromOrderToClaim = {
  kind: WidgetKind.kpi,
  title: 'Time from order to claim',
  metric: 'avgDaysOrderToClaim',
  trend: true,
} as const satisfies WidgetOf<'claimItems'>;

export const byFinalizedDay = 'By the day the request was finalized';

export const finalizedTrend = <const M extends Metric<'claimItems'>>(
  title: string,
  metric: M,
) =>
  ({
    kind: WidgetKind.kpi,
    title,
    metric,
    description: byFinalizedDay,
    trend: true,
    timeField: 'finalizedOn',
  }) as const satisfies WidgetOf<'claimItems'>;

const byLabelScannedDay = 'By the day the label was scanned';

const operations = [
  {
    kind: WidgetKind.kpi,
    title: 'Items in transit',
    description: byLabelScannedDay,
    metric: 'items',
    filters: inTransit,
    timeField: 'inTransitOn',
  },
  {
    kind: WidgetKind.kpi,
    title: 'Return label cost',
    description: byLabelScannedDay,
    metric: 'labelCost',
    timeField: 'inTransitOn',
  },
  {
    kind: WidgetKind.kpi,
    title: 'Average return label cost',
    description: byLabelScannedDay,
    metric: 'avgLabelCost',
    timeField: 'inTransitOn',
  },
  {
    kind: WidgetKind.overTime,
    title: 'Items in transit over time',
    description: 'Items still in transit, by the day their label was scanned',
    mark: ChartMark.bar,
    metrics: ['items'],
    filters: inTransit,
    timeField: 'inTransitOn',
  },
  {
    kind: WidgetKind.ranking,
    title: 'Ready for review reasons',
    dimension: 'reason',
    metric: 'items',
    filters: readyForReview,
  },
] as const satisfies WidgetOf<'claimItems'>[];

export const operationsWidgets = <
  const Trends extends readonly WidgetOf<'claimItems'>[],
>(
  trends: Trends,
) =>
  [timeFromOrderToClaim, ...trends, ...operations, resolutionStatus] as const;
